import type { Organization } from "schema-dts";

/**
 * Organization schema data interface
 */
export interface OrganizationData {
  name: string;
  url: string;
  logo: string;
  description: string;
  foundingDate: string;
  contactPoint?: {
    telephone: string;
    contactType: string;
    availableLanguage: string[];
    areaServed: string[];
  };
  sameAs?: string[];
}

/**
 * Extended Corporation schema data interface (superset of OrganizationData)
 */
export interface CorporationData extends OrganizationData {
  /** Fragment identifier for @id, e.g. "Otrium" → "https://www.otrium.nl/#Otrium" */
  idFragment?: string;
  alternateName?: string;
  legalName?: string;
  image?: string;
  slogan?: string;
  email?: string;
  telephone?: string;
  address?: {
    streetAddress: string;
    postalCode: string;
    addressLocality: string;
    addressRegion: string;
    addressCountry: string;
  };
  returnPolicy?: {
    idUrl: string;
    applicableCountry: string;
    returnPolicyCategory: string;
    returnFees: string;
    returnMethod: string[];
    merchantReturnLink: string;
    merchantReturnDays: number;
  };
}

/**
 * Builds Schema.org Organization structured data
 * @param organizationData - Organization information for schema generation
 * @returns Schema.org Organization object (without @context — injector handles that)
 */
export function buildOrganizationSchema(
  organizationData: OrganizationData
): Organization {
  const organizationSchema: Organization = {
    "@type": "Organization",
    name: organizationData.name,
    url: organizationData.url,
    logo: organizationData.logo,
    description: organizationData.description,
    foundingDate: organizationData.foundingDate,
  };

  if (organizationData.contactPoint) {
    organizationSchema.contactPoint = {
      "@type": "ContactPoint",
      telephone: organizationData.contactPoint.telephone,
      contactType: organizationData.contactPoint.contactType,
      availableLanguage: organizationData.contactPoint.availableLanguage,
      areaServed: organizationData.contactPoint.areaServed,
    };
  }

  if (organizationData.sameAs && organizationData.sameAs.length > 0) {
    organizationSchema.sameAs = organizationData.sameAs;
  }

  return organizationSchema;
}

/**
 * Builds Schema.org Corporation structured data with extended fields
 * including address, return policy, and @id cross-referencing.
 * @param data - Corporation information for schema generation
 * @returns Corporation-shaped object (without @context — injector handles that)
 */
export function buildCorporationSchema(
  data: CorporationData
): Record<string, unknown> {
  const baseUrl = data.url.replace(/\/$/, "");
  const id = data.idFragment
    ? `${baseUrl}/#${data.idFragment}`
    : `${baseUrl}/#${data.name}`;

  const schema: Record<string, unknown> = {
    "@id": id,
    "@type": "Corporation",
    name: data.name,
    url: data.url,
    logo: data.logo,
    foundingDate: data.foundingDate,
  };

  if (data.alternateName) {
    schema.alternateName = data.alternateName;
  }

  if (data.legalName) {
    schema.legalName = data.legalName;
  }

  if (data.image) {
    schema.image = data.image;
  }

  if (data.description) {
    schema.description = data.description;
  }

  if (data.slogan) {
    schema.slogan = data.slogan;
  }

  if (data.email) {
    schema.email = data.email;
  }

  if (data.telephone) {
    schema.telephone = data.telephone;
  }

  if (data.sameAs && data.sameAs.length > 0) {
    schema.sameAs = data.sameAs;
  }

  if (data.address) {
    schema.address = {
      "@type": "PostalAddress",
      streetAddress: data.address.streetAddress,
      postalCode: data.address.postalCode,
      addressLocality: data.address.addressLocality,
      addressRegion: data.address.addressRegion,
      addressCountry: data.address.addressCountry,
    };
  }

  if (data.returnPolicy) {
    schema.hasMerchantReturnPolicy = {
      "@id": data.returnPolicy.idUrl,
      "@type": "MerchantReturnPolicy",
      applicableCountry: data.returnPolicy.applicableCountry,
      returnPolicyCategory: data.returnPolicy.returnPolicyCategory,
      returnFees: data.returnPolicy.returnFees,
      returnMethod: data.returnPolicy.returnMethod,
      merchantReturnLink: data.returnPolicy.merchantReturnLink,
      merchantReturnDays: data.returnPolicy.merchantReturnDays,
    };
  }

  return schema;
}
